import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Monogram } from "./Ornament";
import { easeManor } from "../constants/constants";

const SECRET_HOLD_MS = 4000;

const links = [
  { to: "/", label: "Parlour" },
  { to: "/menu", label: "Menu" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Reserve" },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdFiredRef = useRef(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  const startHold = () => {
    holdFiredRef.current = false;
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      holdFiredRef.current = true;
      navigate("/admin");
    }, SECRET_HOLD_MS);
  };

  const cancelHold = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const handleMonogramClick = (e: React.MouseEvent) => {
    if (holdFiredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      holdFiredRef.current = false;
    }
  };

  return (
    <motion.nav
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: easeManor }}
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[color:var(--parchment)]/92 backdrop-blur-sm shadow-[0_1px_0_var(--gilt-hairline)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" aria-label="Moyle Manor — home" className="flex items-center gap-3">
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: hidden press-and-hold gesture; keyboard users navigate home via the parent Link */}
          <span
            onPointerDown={startHold}
            onPointerUp={cancelHold}
            onPointerLeave={cancelHold}
            onPointerCancel={cancelHold}
            onContextMenu={(e) => e.preventDefault()}
            onClick={handleMonogramClick}
            style={{
              userSelect: "none",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              touchAction: "manipulation",
            }}
            className="inline-flex"
          >
            <Monogram className="text-2xl" />
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-xl text-[color:var(--claret-deep)]">
              Moyle Manor
            </span>
            <span className="u-label mt-1">Est. 1926</span>
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `group relative font-smallcaps tracking-ceremonial text-xs uppercase transition-colors duration-300 ${
                    isActive
                      ? "text-[color:var(--claret-deep)]"
                      : "text-[color:var(--ink-soft)] hover:text-[color:var(--claret)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{l.label}</span>
                    <span
                      className={`absolute -bottom-2 left-1/2 -translate-x-1/2 h-px bg-[color:var(--gilt-deep)] transition-all duration-500 ${
                        isActive ? "w-6" : "w-0 group-hover:w-4"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label="Open menu"
          className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-[color:var(--gilt-hairline)] text-[color:var(--claret)]"
        >
          <span className="relative block w-4 h-3">
            <span
              className={`absolute left-0 top-0 h-px w-full bg-current transition-transform duration-500 ${
                open ? "translate-y-[6px] rotate-45" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-1/2 -translate-y-1/2 h-px w-full bg-current transition-opacity duration-300 ${
                open ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute left-0 bottom-0 h-px w-full bg-current transition-transform duration-500 ${
                open ? "-translate-y-[6px] -rotate-45" : ""
              }`}
            />
          </span>
        </button>
      </div>

      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.5, ease: easeManor }}
        className="md:hidden overflow-hidden bg-[color:var(--parchment)]/95 backdrop-blur-sm"
      >
        <ul className="flex flex-col items-center py-6 gap-5">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `font-smallcaps tracking-ceremonial text-sm uppercase ${
                    isActive ? "text-[color:var(--claret-deep)]" : "text-[color:var(--ink-soft)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </motion.div>
    </motion.nav>
  );
};

export default Navbar;
